import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Select,
  Modal,
  Badge,
  Pagination,
  Alert,
} from '../../components/ui';
import {
  getKalenderKerjaList,
  createKalenderKerja,
  updateKalenderKerja,
  deleteKalenderKerja,
} from '../../features/absensi/api';
import { getSekolahList } from '../../features/master/api';
import { RoleCode, type KalenderKerja } from '@app/shared';
import { useAuth } from '../../features/auth/use-auth';

const JENIS_MAP: Record<string, { label: string; color: 'green' | 'gray' | 'red' }> = {
  hari_kerja: { label: 'Hari Kerja', color: 'green' },
  hari_libur: { label: 'Hari Libur', color: 'gray' },
  libur_nasional: { label: 'Libur Nasional', color: 'red' },
};

type FormState = {
  id?: number;
  sekolah_id: number;
  tanggal: string;
  jenis: 'hari_kerja' | 'hari_libur' | 'libur_nasional';
  keterangan: string;
};

export function KalenderPage() {
  const { user } = useAuth();
  const canManage = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const [rows, setRows] = useState<KalenderKerja[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [jenisFilter, setJenisFilter] = useState('');
  const [sekolahList, setSekolahList] = useState<{ id: number; nama: string }[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    sekolah_id: 0,
    tanggal: '',
    jenis: 'hari_kerja',
    keterangan: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const loadSekolah = async () => {
    try {
      const result = await getSekolahList({ limit: 100 });
      setSekolahList(result.rows.map((s) => ({ id: s.id, nama: s.nama })));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadSekolah();
  }, []);

  const load = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getKalenderKerjaList({
        page,
        limit: 10,
        search: search || undefined,
        jenis: jenisFilter || undefined,
      });
      setRows(result.rows);
      setMeta(result.meta);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [search, jenisFilter]);

  const openCreate = () => {
    setForm({
      sekolah_id: sekolahList[0]?.id ?? 0,
      tanggal: '',
      jenis: 'hari_kerja',
      keterangan: '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row: KalenderKerja) => {
    setForm({
      id: row.id,
      sekolah_id: row.sekolah_id,
      tanggal: row.tanggal,
      jenis: row.jenis,
      keterangan: row.keterangan ?? '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.sekolah_id || !form.tanggal) {
      setFormError('Sekolah dan tanggal wajib diisi');
      return;
    }
    try {
      if (form.id) {
        await updateKalenderKerja(form.id, {
          jenis: form.jenis,
          keterangan: form.keterangan || null,
        });
        setSuccess('Kalender kerja berhasil diperbarui');
      } else {
        await createKalenderKerja({
          sekolah_id: form.sekolah_id,
          tanggal: form.tanggal,
          jenis: form.jenis,
          keterangan: form.keterangan || null,
        });
        setSuccess('Kalender kerja berhasil dibuat');
      }
      setModalOpen(false);
      load(meta.page);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Gagal menyimpan');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Yakin ingin menghapus data ini?')) return;
    try {
      await deleteKalenderKerja(id);
      setSuccess('Kalender kerja berhasil dihapus');
      load(meta.page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus');
    }
  };

  const fmtDate = (d: string) => {
    if (!d) return '-';
    const parts = d.split('-');
    if (parts.length !== 3) return d;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const getSekolahNama = (id: number) => {
    return sekolahList.find((s) => s.id === id)?.nama ?? '-';
  };

  return (
    <AdminLayout title="Kalender Kerja">
      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}
      <Card>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <Input
            placeholder="Cari keterangan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          <Select
            value={jenisFilter}
            onChange={(e) => setJenisFilter(e.target.value)}
            style={{ width: '160px' }}
          >
            <option value="">Semua jenis</option>
            <option value="hari_kerja">Hari Kerja</option>
            <option value="hari_libur">Hari Libur</option>
            <option value="libur_nasional">Libur Nasional</option>
          </Select>
          {canManage && <Button onClick={openCreate}>Tambah Kalender</Button>}
        </div>
        <Table>
          <thead>
            <tr>
              <Th>Sekolah</Th>
              <Th>Tanggal</Th>
              <Th>Jenis</Th>
              <Th>Keterangan</Th>
              {canManage && <Th style={{ textAlign: 'right' }}>Aksi</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>{getSekolahNama(row.sekolah_id)}</Td>
                <Td>{fmtDate(row.tanggal)}</Td>
                <Td>
                  <Badge color={JENIS_MAP[row.jenis]?.color ?? 'gray'}>
                    {JENIS_MAP[row.jenis]?.label ?? row.jenis}
                  </Badge>
                </Td>
                <Td>{row.keterangan || '-'}</Td>
                {canManage && (
                  <Td style={{ textAlign: 'right' }}>
                    <Button variant="secondary" onClick={() => openEdit(row)}>
                      Ubah
                    </Button>{' '}
                    <Button variant="danger" onClick={() => handleDelete(row.id)}>
                      Hapus
                    </Button>
                  </Td>
                )}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <Td colSpan={canManage ? 5 : 4} style={{ textAlign: 'center' }}>
                  Tidak ada data
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        {loading && <p style={{ textAlign: 'center', marginTop: '1rem' }}>Memuat...</p>}
        <Pagination page={meta.page} totalPages={meta.total_pages} onChange={load} />
      </Card>

      <Modal
        open={modalOpen && canManage}
        onClose={() => setModalOpen(false)}
        title={form.id ? 'Ubah Kalender Kerja' : 'Tambah Kalender Kerja'}
      >
        <form onSubmit={handleSubmit}>
          {formError && <Alert type="error">{formError}</Alert>}
          <Select
            label="Sekolah"
            value={String(form.sekolah_id)}
            onChange={(e) => setForm({ ...form, sekolah_id: Number(e.target.value) })}
            disabled={!!form.id}
            required
          >
            <option value="0">Pilih sekolah</option>
            {sekolahList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nama}
              </option>
            ))}
          </Select>
          <Input
            label="Tanggal"
            type="date"
            value={form.tanggal}
            onChange={(e) => setForm({ ...form, tanggal: e.target.value })}
            disabled={!!form.id}
            required
          />
          <Select
            label="Jenis"
            value={form.jenis}
            onChange={(e) => setForm({ ...form, jenis: e.target.value as FormState['jenis'] })}
          >
            <option value="hari_kerja">Hari Kerja</option>
            <option value="hari_libur">Hari Libur</option>
            <option value="libur_nasional">Libur Nasional</option>
          </Select>
          <Input
            label="Keterangan"
            value={form.keterangan}
            onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <Button variant="secondary" onClick={() => setModalOpen(false)} type="button">
              Batal
            </Button>
            <Button type="submit">Simpan</Button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
}
